import React, { useEffect, useRef, useState } from 'react';
import { Award, CheckCircle, Download, Loader, MapPin, Plus, Search, SlidersHorizontal, Star } from 'lucide-react';

import { Alert } from '../shared';
import { turnoverItApi } from '../../services/api';
import { createUuid } from '../../utils/uuid';

const SEARCH_MESSAGES = {
  SEARCH_CRITERIA_INVALID: 'Certains critères ne sont pas valides. Corrigez les champs signalés avant de relancer la recherche.',
  TURNOVER_QUOTA_REACHED: 'Le quota Turnover-IT ne permet pas une nouvelle recherche pour le moment.',
  TURNOVER_RESPONSE_INVALID: 'Turnover-IT a renvoyé une réponse inexploitable. Aucun profil n’est affiché.',
  TURNOVER_UNAVAILABLE: 'Turnover-IT est temporairement indisponible. Vos critères sont conservés.',
  SELECTION_STORE_UNAVAILABLE: 'Le service de sélection est temporairement indisponible. Aucun profil n’a été affiché.',
  TURNOVER_CONFIGURATION_UNAVAILABLE: 'Turnover-IT est temporairement indisponible. Contactez le support si le problème persiste.',
  SEARCH_OUTCOME_UNKNOWN: 'L’issue de cette recherche est incertaine. Aucun nouvel appel automatique n’a été effectué.',
  SEARCH_REQUEST_ID_REUSED: 'Cette tentative ne correspond plus aux critères affichés. Revenez aux critères pour lancer une nouvelle recherche.',
};

const IMPORT_MESSAGES = {
  TURNOVER_SELECTION_EXPIRED: 'Cette sélection a expiré. Relancez la recherche pour importer le profil.',
  IMPORT_RATE_LIMITED: 'Trop de demandes d’import sont en cours. Réessayez plus tard ; aucun nouvel import ne sera lancé automatiquement.',
  TURNOVER_CV_INVALID: 'Le CV fourni ne peut pas être importé en sécurité. Aucun candidat n’a été ajouté.',
  TURNOVER_CV_UNAVAILABLE: 'Le CV est temporairement indisponible. Aucun doublon ne sera créé si vous réessayez.',
  DURABLE_STORAGE_UNAVAILABLE: 'L’import est temporairement indisponible. Aucun doublon ne sera créé si vous réessayez.',
  CANDIDATE_STORE_UNAVAILABLE: 'L’import est temporairement indisponible. Aucun doublon ne sera créé si vous réessayez.',
  ANALYSIS_QUEUE_UNAVAILABLE: 'L’import est temporairement indisponible. Aucun doublon ne sera créé si vous réessayez.',
  PROVIDER_IDENTITY_CONFLICT: 'Ce profil correspond à des identités incompatibles. L’import nécessite une vérification.',
  IMPORT_STATE_CONFLICT: 'L’import ne peut pas être repris automatiquement. Contactez le support avec la référence affichée.',
};

const clone = value => JSON.parse(JSON.stringify(value));

const FILTER_GROUPS = [
  {
    field: 'experienceYears',
    label: 'Expérience',
    options: [
      { value: 'less_than_1_year', label: 'Moins d’un an' },
      { value: '1-2_years', label: '1 à 2 ans' },
      { value: '3-4_years', label: '3 à 4 ans' },
      { value: '5-10_years', label: '5 à 10 ans' },
      { value: '11-15_years', label: '11 à 15 ans' },
      { value: 'more_than_15_years', label: 'Plus de 15 ans' },
    ],
  },
  {
    field: 'remoteModes',
    label: 'Télétravail',
    options: [
      { value: 'partial', label: 'Hybride' },
      { value: 'full', label: '100 % télétravail' },
      { value: 'none', label: 'Sur site' },
    ],
  },
  {
    field: 'contracts',
    label: 'Contrats',
    options: [
      { value: 'FREELANCE', label: 'Freelance' },
      { value: 'PERMANENT', label: 'CDI' },
      { value: 'TEMPORARY', label: 'CDD' },
      { value: 'INTERNSHIP', label: 'Stage' },
      { value: 'APPRENTICESHIP', label: 'Alternance' },
      { value: 'INTERCONTRACT', label: 'Intercontrat' },
    ],
  },
  {
    field: 'availabilities',
    label: 'Disponibilité',
    options: [
      { value: 'immediate', label: 'Immédiate' },
      { value: 'within_1_month', label: 'Sous 1 mois' },
      { value: 'within_2_month', label: 'Sous 2 mois' },
      { value: 'within_3_month', label: 'Sous 3 mois' },
      { value: 'more_than_3_month', label: 'Plus de 3 mois' },
    ],
  },
];

const uniqueValues = values => [...new Set(values.filter(Boolean))];

const prepareCriteria = response => {
  const source = clone(response.criteria);
  const skillOptions = {
    requiredSkills: uniqueValues(source.requiredSkills || []),
    optionalSkills: uniqueValues(source.optionalSkills || []),
  };
  return {
    criteria: { ...source, requiredSkills: [], optionalSkills: [] },
    skillOptions,
  };
};

const SkillChoiceGroup = ({
  title,
  description,
  kind,
  icon: Icon,
  options,
  selected,
  onToggle,
  onAdd,
  tone,
}) => {
  const [input, setInput] = useState('');
  const required = kind === 'requise';
  const styles = required
    ? { panel: 'from-red-50 to-pink-50 border-red-200', icon: 'text-red-500', badge: 'bg-red-100 text-red-800', selected: 'border-red-300 bg-red-100 text-red-900' }
    : { panel: 'from-green-50 to-emerald-50 border-green-200', icon: 'text-green-600', badge: 'bg-green-100 text-green-800', selected: 'border-green-300 bg-green-100 text-green-900' };

  const submit = () => {
    const value = input.trim();
    if (!value) return;
    onAdd(value);
    setInput('');
  };

  return (
    <fieldset className={`rounded-xl border bg-gradient-to-br p-5 ${styles.panel}`}>
      <legend className="sr-only">{title}</legend>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h4 className="flex items-center text-base font-bold text-gray-900">
            <Icon className={`mr-2 ${styles.icon}`} size={18} aria-hidden="true" />
            {title}
          </h4>
          <p className="mt-1 text-sm text-gray-600">{description}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${styles.badge}`}>
          {selected.length} sélectionnée{selected.length > 1 ? 's' : ''}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {options.map(skill => {
          const checked = selected.includes(skill);
          return (
            <label
              key={skill}
              className={`flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm font-medium transition-colors focus-within:ring-2 focus-within:ring-blue-600 ${checked ? styles.selected : 'border-white bg-white text-gray-700 hover:border-gray-300'}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(skill)}
                aria-label={`Sélectionner ${skill} comme compétence ${kind}`}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
              />
              <span>{skill}</span>
            </label>
          );
        })}
      </div>

      <div className="mt-4 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={event => setInput(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
          aria-label={`Ajouter une compétence ${kind}`}
          placeholder="Ajouter une autre compétence"
          className="min-h-[44px] min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-600"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!input.trim()}
          aria-label={`Ajouter la compétence ${kind}`}
          className={`min-h-[44px] min-w-[44px] rounded-lg px-3 text-white focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 disabled:cursor-not-allowed disabled:opacity-50 ${tone}`}
        >
          <Plus size={16} className="mx-auto" aria-hidden="true" />
        </button>
      </div>
    </fieldset>
  );
};

const MultiChoiceGroup = ({ label, options, selected, onToggle }) => {
  const choices = options;
  return (
    <fieldset className="rounded-xl border border-gray-200 bg-white p-4">
      <legend className="px-1 text-sm font-semibold text-gray-800">{label}</legend>
      <p className="mb-3 text-xs text-gray-500">Plusieurs choix possibles</p>
      <div className="space-y-2">
        {choices.map(option => {
          const checked = selected.includes(option.value);
          return (
            <label key={option.value} className={`flex min-h-[40px] cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors focus-within:ring-2 focus-within:ring-blue-600 ${checked ? 'border-blue-300 bg-blue-50 font-medium text-blue-900' : 'border-gray-200 text-gray-700 hover:border-blue-200'}`}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(option.value)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
              />
              {option.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
};

const LocationCombobox = ({ needId, items, labels, onAdd, onRemove }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [status, setStatus] = useState('idle');
  const [activeIndex, setActiveIndex] = useState(-1);
  const requestSequence = useRef(0);
  const listboxId = 'turnover-location-suggestions';
  const open = query.trim().length >= 2 && (status !== 'idle' || suggestions.length > 0);

  useEffect(() => {
    const normalized = query.trim();
    if (normalized.length < 2) {
      requestSequence.current += 1;
      setSuggestions([]);
      setStatus('idle');
      setActiveIndex(-1);
      return undefined;
    }

    const requestId = requestSequence.current + 1;
    requestSequence.current = requestId;
    setStatus('loading');
    setActiveIndex(-1);
    const timer = setTimeout(() => {
      turnoverItApi.searchLocations(needId, normalized).then(response => {
        if (requestSequence.current !== requestId) return;
        setSuggestions(response.items || []);
        setStatus('ready');
      }).catch(() => {
        if (requestSequence.current !== requestId) return;
        setSuggestions([]);
        setStatus('error');
      });
    }, 300);
    return () => {
      clearTimeout(timer);
      if (requestSequence.current === requestId) requestSequence.current += 1;
    };
  }, [needId, query]);

  const selectSuggestion = suggestion => {
    onAdd(suggestion.value, suggestion.shortLabel || suggestion.label);
    setQuery('');
  };

  const handleKeyDown = event => {
    if (event.key === 'ArrowDown' && suggestions.length) {
      event.preventDefault();
      setActiveIndex(index => (index + 1) % suggestions.length);
      return;
    }
    if (event.key === 'ArrowUp' && suggestions.length) {
      event.preventDefault();
      setActiveIndex(index => (index <= 0 ? suggestions.length - 1 : index - 1));
      return;
    }
    if (event.key === 'Escape') {
      requestSequence.current += 1;
      setSuggestions([]);
      setStatus('idle');
      setActiveIndex(-1);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (suggestions.length) selectSuggestion(suggestions[activeIndex >= 0 ? activeIndex : 0]);
    }
  };

  return (
    <div>
      <div>
        <input
          type="text"
          role="combobox"
          aria-label="Ajouter une localisation"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined}
          value={query}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Rechercher une ville ou un département"
          className="min-h-[44px] min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-600"
        />
      </div>
      {open && (
        <div className="relative">
          <ul id={listboxId} role="listbox" className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white p-1 shadow-lg">
            {status === 'loading' && <li className="px-3 py-2 text-sm text-gray-600" aria-live="polite">Recherche des localisations…</li>}
            {status === 'error' && <li className="px-3 py-2 text-sm text-red-700" role="status">Suggestions indisponibles. Réessayez plus tard.</li>}
            {status === 'ready' && suggestions.length === 0 && <li className="px-3 py-2 text-sm text-gray-600" role="status">Aucune localisation Turnover-IT trouvée.</li>}
            {suggestions.map((suggestion, index) => (
              <li
                id={`${listboxId}-${index}`}
                key={suggestion.value}
                role="option"
                aria-selected={activeIndex === index}
                onMouseDown={event => event.preventDefault()}
                onClick={() => selectSuggestion(suggestion)}
                className={`cursor-pointer rounded-md px-3 py-2 text-sm ${activeIndex === index ? 'bg-blue-100 text-blue-900' : 'text-gray-800 hover:bg-blue-50'}`}
              >
                <span className="block font-medium">{suggestion.shortLabel || suggestion.label}</span>
                {suggestion.shortLabel && suggestion.label !== suggestion.shortLabel && <span className="block text-xs text-gray-500">{suggestion.label}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!!items.length && (
        <div className="mt-3 flex flex-wrap gap-2">
          {items.map(item => (
            <span key={item} className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-800">
              <MapPin size={14} className="mr-1" aria-hidden="true" />
              {labels[item] || item}
              <button
                type="button"
                onClick={() => onRemove(item)}
                aria-label={`Retirer la localisation ${labels[item] || item}`}
                className="ml-2 text-blue-600 hover:text-blue-800 focus:ring-2 focus:ring-blue-600"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const createSearchRequestId = createUuid;

const getApiError = error => {
  const payload = error?.response?.data?.error || {};
  return {
    code: payload.code || 'UNKNOWN_ERROR',
    requestId: payload.requestId,
  };
};

const TurnoverCandidateTab = ({
  active,
  needId,
  onBusyChange,
  onProcessing,
  onReady,
  onBatchImported,
  analysisError,
}) => {
  const [view, setView] = useState('idle');
  const [criteria, setCriteria] = useState(null);
  const [skillOptions, setSkillOptions] = useState({ requiredSkills: [], optionalSkills: [] });
  const [locationLabels, setLocationLabels] = useState({});
  const [limit, setLimit] = useState(10);
  const [results, setResults] = useState(null);
  const [selectedTokens, setSelectedTokens] = useState([]);
  const [searchAttempt, setSearchAttempt] = useState(null);
  const [error, setError] = useState(null);
  const [expirationMessage, setExpirationMessage] = useState(null);
  const [downloadingToken, setDownloadingToken] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [batchFeedback, setBatchFeedback] = useState(null);
  const [pendingBatchRefresh, setPendingBatchRefresh] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const loadedNeedId = useRef(null);
  const searchInFlight = useRef(false);
  const importInFlight = useRef(false);
  const titleRef = useRef(null);
  const radiusRef = useRef(null);
  const salaryRef = useRef(null);
  const stateHeadingRef = useRef(null);

  const loadCriteria = async () => {
    if (!needId) return;
    setView('criteriaLoading');
    setError(null);
    try {
      const response = await turnoverItApi.getSearchCriteria(needId);
      const prepared = prepareCriteria(response);
      setCriteria(clone(prepared.criteria));
      setSkillOptions(clone(prepared.skillOptions));
      setLocationLabels(Object.fromEntries((prepared.criteria.locations || []).map(location => [location, location])));
      setLimit(10);
      setResults(null);
      setSelectedTokens([]);
      setBatchFeedback(null);
      setPendingBatchRefresh(null);
      setSearchAttempt(null);
      setExpirationMessage(null);
      loadedNeedId.current = needId;
      setView('criteria');
    } catch (_error) {
      setView('criteriaError');
    }
  };

  useEffect(() => {
    if (!active || !needId || loadedNeedId.current === needId) return;
    let current = true;
    setView('criteriaLoading');
    setError(null);
    turnoverItApi.getSearchCriteria(needId).then(response => {
      if (!current) return;
      const prepared = prepareCriteria(response);
      setCriteria(clone(prepared.criteria));
      setSkillOptions(clone(prepared.skillOptions));
      setLocationLabels(Object.fromEntries((prepared.criteria.locations || []).map(location => [location, location])));
      setLimit(10);
      setResults(null);
      setSelectedTokens([]);
      setBatchFeedback(null);
      setPendingBatchRefresh(null);
      setSearchAttempt(null);
      setExpirationMessage(null);
      loadedNeedId.current = needId;
      setView('criteria');
    }).catch(() => {
      if (current) setView('criteriaError');
    });
    return () => {
      current = false;
    };
  }, [active, needId]);

  useEffect(() => {
    if (loadedNeedId.current && loadedNeedId.current !== needId) {
      loadedNeedId.current = null;
      setView('idle');
      setCriteria(null);
      setLocationLabels({});
      setResults(null);
      setSelectedTokens([]);
      setBatchFeedback(null);
      setPendingBatchRefresh(null);
    }
  }, [needId]);

  useEffect(() => {
    if (!analysisError) return;
    setError({
      code: 'ANALYSIS_FAILED',
      message: analysisError,
    });
    setView('importError');
    setTimeout(() => stateHeadingRef.current?.focus(), 0);
  }, [analysisError]);

  const updateCriteria = (field, value) => {
    setCriteria(previous => ({ ...previous, [field]: value }));
    setError(null);
    setResults(null);
    setSelectedTokens([]);
    setBatchFeedback(null);
    setPendingBatchRefresh(null);
    setSearchAttempt(null);
  };

  const addSkill = (field, otherField, skill) => {
    const normalized = skill.trim();
    if (!normalized) return;
    const existing = [...skillOptions.requiredSkills, ...skillOptions.optionalSkills]
      .find(value => value.toLocaleLowerCase() === normalized.toLocaleLowerCase());
    const value = existing || normalized;
    setSkillOptions(previous => ({
      ...previous,
      [field]: uniqueValues([...previous[field], value]),
      [otherField]: previous[otherField].filter(item => item !== value),
    }));
    toggleSkill(field, otherField, value, true);
  };

  const toggleSkill = (field, otherField, skill, forceSelected = false) => {
    setCriteria(previous => {
      const selected = previous[field] || [];
      const alreadySelected = selected.includes(skill);
      return {
        ...previous,
        [field]: alreadySelected && !forceSelected ? selected.filter(value => value !== skill) : uniqueValues([...selected, skill]),
        [otherField]: (previous[otherField] || []).filter(value => value !== skill),
      };
    });
    setError(null);
    setResults(null);
    setSelectedTokens([]);
    setBatchFeedback(null);
    setPendingBatchRefresh(null);
    setSearchAttempt(null);
  };

  const toggleCriterion = (field, value) => {
    const selected = criteria[field] || [];
    updateCriteria(field, selected.includes(value)
      ? selected.filter(item => item !== value)
      : [...selected, value]);
  };

  const validateCriteria = () => {
    const title = (criteria.profileJobTitle || '').trim();
    const skills = [...(criteria.requiredSkills || []), ...(criteria.optionalSkills || [])];
    if (!title && skills.length === 0) {
      setError({ field: 'profileJobTitle', message: 'Ajoutez au moins une compétence ou un intitulé de poste.' });
      titleRef.current?.focus();
      return false;
    }
    if (title.length > 250) {
      setError({ field: 'profileJobTitle', message: 'L’intitulé de poste ne peut pas dépasser 250 caractères.' });
      titleRef.current?.focus();
      return false;
    }
    if (skills.join(' ').length > 512) {
      setError({ field: 'skills', message: 'Les compétences dépassent la limite autorisée. Retirez ou raccourcissez certaines compétences.' });
      return false;
    }
    const radius = criteria.locationRadius;
    if (radius !== null && radius !== '' && (!Number.isInteger(Number(radius)) || Number(radius) < 0)) {
      setError({ field: 'locationRadius', message: 'Saisissez un rayon valide en kilomètres.' });
      radiusRef.current?.focus();
      return false;
    }
    const dailySalary = criteria.maxDailySalary;
    if (dailySalary !== null && dailySalary !== '' && (!Number.isInteger(Number(dailySalary)) || Number(dailySalary) < 0)) {
      setError({ field: 'maxDailySalary', message: 'Saisissez un TJM candidat maximum valide.' });
      salaryRef.current?.focus();
      return false;
    }
    return true;
  };

  const runSearch = async attempt => {
    if (searchInFlight.current) return;
    searchInFlight.current = true;
    setView('searching');
    setError(null);
    try {
      const response = await turnoverItApi.searchProfiles(needId, attempt.body);
      const items = (response.items || []).slice(0, attempt.body.limit);
      setResults({ ...response, items, count: items.length });
      setSelectedTokens([]);
      setBatchFeedback(null);
      setPendingBatchRefresh(null);
      setView(response.items?.length ? 'results' : 'empty');
      setTimeout(() => stateHeadingRef.current?.focus(), 0);
    } catch (requestError) {
      const apiError = getApiError(requestError);
      setError({
        ...apiError,
        message: SEARCH_MESSAGES[apiError.code] || 'La recherche n’a pas pu aboutir. Vos critères sont conservés.',
      });
      setView('searchError');
    } finally {
      searchInFlight.current = false;
    }
  };

  const submitSearch = event => {
    event.preventDefault();
    if (searchInFlight.current || !validateCriteria()) return;
    const body = {
      searchRequestId: createSearchRequestId(),
      criteria: clone(criteria),
      limit,
    };
    const attempt = { body };
    setSearchAttempt(attempt);
    runSearch(attempt);
  };

  const retrySearch = () => {
    if (!searchAttempt || searchInFlight.current) return;
    const attempt = {
      body: {
        ...clone(searchAttempt.body),
        searchRequestId: createSearchRequestId(),
      },
    };
    setSearchAttempt(attempt);
    runSearch(attempt);
  };

  const returnToCriteria = () => {
    setView('criteria');
    setError(null);
    setSearchAttempt(null);
    setTimeout(() => stateHeadingRef.current?.focus(), 0);
  };

  const runImport = async () => {
    if (!selectedTokens.length || importInFlight.current) return;
    if (selectedTokens.length > 1) {
      importInFlight.current = true;
      setView('importing');
      setError(null);
      setBatchFeedback(null);
      onBusyChange(true);
      const successes = [];
      const failures = [];
      try {
        for (const [index, token] of selectedTokens.entries()) {
          const item = results?.items?.find(result => result.selectionToken === token);
          setImportProgress({ completed: index, total: selectedTokens.length, displayName: item?.displayName });
          try {
            const response = await turnoverItApi.importProfile(needId, token);
            successes.push({ token, response: response.data });
          } catch (requestError) {
            const apiError = getApiError(requestError);
            failures.push({
              token,
              code: apiError.code,
              requestId: apiError.requestId,
              message: IMPORT_MESSAGES[apiError.code]
                || 'L’import n’a pas pu aboutir. Aucun nouvel essai n’a été lancé automatiquement.',
            });
          }
        }
        const completion = await onBatchImported(successes, failures);
        const refreshFailed = completion?.refreshFailed === true;
        if (!failures.length && !refreshFailed) return;

        const successfulTokens = new Set(successes.map(item => item.token));
        setResults(previous => {
          const items = previous.items.filter(item => !successfulTokens.has(item.selectionToken));
          return { ...previous, items, count: items.length };
        });
        setSelectedTokens(failures.map(item => item.token));
        setPendingBatchRefresh(refreshFailed ? { successes, failures } : null);
        setBatchFeedback({
          message: refreshFailed
            ? `${successes.length} ${successes.length === 1 ? 'profil ajouté' : 'profils ajoutés'}. La liste des candidats n’a pas pu être actualisée.`
            : `${successes.length} ${successes.length === 1 ? 'profil ajouté' : 'profils ajoutés'}. ${failures.length} ${failures.length === 1 ? 'import a échoué' : 'imports ont échoué'}.`,
        });
        setView('results');
      } finally {
        importInFlight.current = false;
        setImportProgress(null);
        onBusyChange(false);
      }
      return;
    }

    const selectedToken = selectedTokens[0];
    importInFlight.current = true;
    setView('importing');
    setError(null);
    onBusyChange(true);
    try {
      const response = await turnoverItApi.importProfile(needId, selectedToken);
      if (response.status === 200 || response.data?.status === 'READY') {
        await onReady(response.data);
        onBusyChange(false);
        return;
      }
      setView('analyzing');
      onProcessing(response.data);
    } catch (requestError) {
      const apiError = getApiError(requestError);
      const message = IMPORT_MESSAGES[apiError.code]
        || 'L’import n’a pas pu aboutir. Aucun nouvel essai n’a été lancé automatiquement.';
      onBusyChange(false);
      if (apiError.code === 'TURNOVER_SELECTION_EXPIRED') {
        setResults(null);
        setSelectedTokens([]);
        setSearchAttempt(null);
        setExpirationMessage(message);
        setView('criteria');
        setTimeout(() => stateHeadingRef.current?.focus(), 0);
        return;
      }
      setError({ ...apiError, message });
      setView('importError');
    } finally {
      importInFlight.current = false;
    }
  };

  const retryBatchRefresh = async () => {
    if (!pendingBatchRefresh || importInFlight.current) return;
    importInFlight.current = true;
    onBusyChange(true);
    try {
      const completion = await onBatchImported(
        pendingBatchRefresh.successes,
        pendingBatchRefresh.failures,
      );
      if (completion?.refreshFailed) return;
      setPendingBatchRefresh(null);
      if (pendingBatchRefresh.failures.length) {
        const failures = pendingBatchRefresh.failures.length;
        setBatchFeedback({
          message: `${failures} ${failures === 1 ? 'import a échoué' : 'imports ont échoué'}.`,
        });
      }
    } finally {
      importInFlight.current = false;
      onBusyChange(false);
    }
  };

  const downloadCv = async item => {
    if (downloadingToken) return;
    setDownloadingToken(item.selectionToken);
    setDownloadError(null);
    try {
      const { blob, filename } = await turnoverItApi.downloadProfileCv(needId, item.selectionToken);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      const apiError = getApiError(requestError);
      setDownloadError(
        apiError.code === 'TURNOVER_SELECTION_EXPIRED'
          ? 'Cette sélection a expiré. Relancez la recherche pour télécharger le CV.'
          : 'Le CV ne peut pas être téléchargé pour le moment. Vous pouvez conserver la sélection et réessayer.',
      );
    } finally {
      setDownloadingToken(null);
    }
  };

  const searchCanRetry = error && ![
    'SEARCH_CRITERIA_INVALID',
    'TURNOVER_QUOTA_REACHED',
    'TURNOVER_CONFIGURATION_UNAVAILABLE',
    'SEARCH_OUTCOME_UNKNOWN',
    'SEARCH_REQUEST_ID_REUSED',
  ].includes(error.code);
  const importCanRetry = error && ![
    'TURNOVER_CV_INVALID',
    'PROVIDER_IDENTITY_CONFLICT',
    'IMPORT_STATE_CONFLICT',
  ].includes(error.code);

  const selectedItems = results?.items?.filter(item => selectedTokens.includes(item.selectionToken)) || [];
  const selectedItem = selectedItems[0];

  return (
    <section
      id="candidate-tabpanel-turnover"
      role="tabpanel"
      aria-labelledby="candidate-tab-turnover"
      hidden={!active}
      className="space-y-6"
    >
      {!needId ? (
        <Alert variant="error">
          Impossible d’ouvrir Turnover-IT sans besoin sélectionné. Fermez cette fenêtre et réessayez depuis un besoin.
        </Alert>
      ) : view === 'idle' || view === 'criteriaLoading' ? (
        <div className="py-10 text-center" aria-live="polite" aria-busy="true">
          <Loader className="mx-auto mb-3 animate-spin text-blue-600" aria-hidden="true" />
          <p>Préparation des critères à partir du besoin…</p>
        </div>
      ) : view === 'criteriaError' ? (
        <div role="alert" className="space-y-4">
          <Alert variant="error">
            Les données du besoin sont temporairement indisponibles. Aucun appel Turnover-IT n’a été effectué.
          </Alert>
          <button type="button" onClick={loadCriteria} className="min-h-[44px] px-4 py-2 rounded-lg bg-blue-600 text-white focus:ring-2 focus:ring-offset-2 focus:ring-blue-600">
            Réessayer
          </button>
        </div>
      ) : view === 'criteria' ? (
        <form onSubmit={submitSearch} className="space-y-6">
          {expirationMessage && <div ref={stateHeadingRef} tabIndex="-1" role="alert"><Alert variant="error">{expirationMessage}</Alert></div>}
          {error?.message && <div id="turnover-form-error" role="alert"><Alert variant="error">{error.message}</Alert></div>}

          <section className="rounded-xl border border-gray-200 bg-gray-50 p-5">
            <label htmlFor="turnover-title" className="mb-2 block text-sm font-semibold text-gray-800">Intitulé de poste</label>
            <input
              ref={titleRef}
              id="turnover-title"
              value={criteria.profileJobTitle || ''}
              onChange={event => updateCriteria('profileJobTitle', event.target.value)}
              maxLength="250"
              aria-invalid={error?.field === 'profileJobTitle' || undefined}
              aria-describedby={error?.field === 'profileJobTitle' ? 'turnover-form-error' : undefined}
              className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            />
            <p className="mt-2 text-xs text-gray-500">Prérempli depuis l’intitulé ou l’analyse du besoin.</p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SkillChoiceGroup
              title="Compétences requises"
              description="Suggestions du besoin, décochées par défaut."
              kind="requise"
              icon={Star}
              options={skillOptions.requiredSkills}
              selected={criteria.requiredSkills || []}
              onToggle={skill => toggleSkill('requiredSkills', 'optionalSkills', skill)}
              onAdd={skill => addSkill('requiredSkills', 'optionalSkills', skill)}
              tone="bg-red-600 hover:bg-red-700"
            />
            <SkillChoiceGroup
              title="Compétences optionnelles"
              description="Suggestions complémentaires, à activer si pertinent."
              kind="optionnelle"
              icon={Award}
              options={skillOptions.optionalSkills}
              selected={criteria.optionalSkills || []}
              onToggle={skill => toggleSkill('optionalSkills', 'requiredSkills', skill)}
              onAdd={skill => addSkill('optionalSkills', 'requiredSkills', skill)}
              tone="bg-green-600 hover:bg-green-700"
            />
          </div>

          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                <MapPin size={18} aria-hidden="true" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900">Zone de recherche</h4>
                <p className="text-sm text-gray-600">Sélectionnez une ou plusieurs localisations proposées par Turnover-IT.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,2fr)_minmax(180px,1fr)]">
              <div>
                <span className="mb-2 block text-sm font-semibold text-gray-800">Localisations</span>
                <LocationCombobox
                  needId={needId}
                  items={criteria.locations || []}
                  labels={locationLabels}
                  onAdd={(location, label) => {
                    const normalized = location.trim();
                    if (!normalized || criteria.locations.some(value => value.toLocaleLowerCase() === normalized.toLocaleLowerCase())) return;
                    setLocationLabels(previous => ({ ...previous, [normalized]: label }));
                    updateCriteria('locations', [...criteria.locations, normalized]);
                  }}
                  onRemove={location => {
                    setLocationLabels(previous => {
                      const next = { ...previous };
                      delete next[location];
                      return next;
                    });
                    updateCriteria('locations', criteria.locations.filter(item => item !== location));
                  }}
                />
              </div>
              <div>
                <label htmlFor="turnover-radius" className="mb-2 block text-sm font-semibold text-gray-800">Rayon</label>
                <div className="flex items-center gap-2">
                  <input
                    ref={radiusRef}
                    id="turnover-radius"
                    type="number"
                    min="0"
                    step="1"
                    value={criteria.locationRadius ?? ''}
                    disabled={!criteria.locations?.length}
                    onChange={event => updateCriteria('locationRadius', event.target.value === '' ? null : Number(event.target.value))}
                    aria-invalid={error?.field === 'locationRadius' || undefined}
                    aria-describedby={error?.field === 'locationRadius' ? 'turnover-form-error' : undefined}
                    className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 disabled:bg-gray-100"
                  />
                  <span className="text-sm text-gray-600">km</span>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-gray-50 p-5">
            <div className="mb-4 flex items-center gap-2">
              <SlidersHorizontal size={18} className="text-blue-600" aria-hidden="true" />
              <div>
                <h4 className="font-bold text-gray-900">Filtres complémentaires</h4>
                <p className="text-sm text-gray-600">Les champs acceptés par Turnover peuvent être combinés.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {FILTER_GROUPS.map(group => (
                <MultiChoiceGroup
                  key={group.field}
                  label={group.label}
                  options={group.options}
                  selected={criteria[group.field] || []}
                  onToggle={value => toggleCriterion(group.field, value)}
                />
              ))}
            </div>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-xl border border-gray-200 bg-white p-5">
            <div>
              <label htmlFor="turnover-tjm" className="block text-sm font-semibold text-gray-800 mb-2">TJM candidat maximum</label>
              <div className="flex items-center gap-2">
                <input
                  ref={salaryRef}
                  id="turnover-tjm"
                  type="number"
                  min="0"
                  step="1"
                  value={criteria.maxDailySalary ?? ''}
                  onChange={event => updateCriteria('maxDailySalary', event.target.value === '' ? null : Number(event.target.value))}
                  aria-invalid={error?.field === 'maxDailySalary' || undefined}
                  aria-describedby={error?.field === 'maxDailySalary' ? 'turnover-form-error' : undefined}
                  className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                />
                <span className="text-sm text-gray-600">€ / jour</span>
              </div>
              <p className="mt-1 text-sm text-gray-600">Le TJM candidat n’est pas déduit du TJM de facturation du besoin.</p>
            </div>
            <fieldset>
              <legend className="block text-sm font-semibold text-gray-800 mb-2">Nombre de profils</legend>
              <div className="flex gap-4" role="radiogroup" aria-label="Nombre de profils">
                {[5, 10].map(value => (
                  <label key={value} className={`min-h-[44px] min-w-[72px] inline-flex items-center justify-center gap-2 px-3 border rounded-lg cursor-pointer focus-within:ring-2 focus-within:ring-blue-600 ${limit === value ? 'border-blue-300 bg-blue-50 text-blue-900 font-medium' : 'border-gray-300'}`}>
                    <input type="radio" name="turnover-limit" value={value} checked={limit === value} onChange={() => setLimit(value)} />
                    {value}
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          <div className="sticky bottom-0 -mx-1 flex justify-end border-t border-gray-200 bg-white/95 px-1 pt-4 backdrop-blur-sm">
            <button type="submit" className="min-h-[44px] inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-600">
              <Search size={16} className="mr-2" aria-hidden="true" />
              Rechercher {limit} profils
            </button>
          </div>
        </form>
      ) : view === 'searching' ? (
        <div aria-live="polite" aria-busy="true" className="py-10 text-center space-y-2">
          <Loader className="mx-auto animate-spin text-blue-600" aria-hidden="true" />
          <p>Recherche de {searchAttempt?.body.limit} profils sur Turnover-IT…</p>
          <p>Cette recherche peut consommer des crédits.</p>
          <p className="text-sm text-gray-600">Aucun nouvel appel ne sera lancé automatiquement.</p>
        </div>
      ) : view === 'searchError' ? (
        <div role="alert" className="space-y-4">
          <Alert variant="error">{error.message}</Alert>
          {error.requestId && <p className="text-sm">Référence : {error.requestId}</p>}
          <div className="flex flex-col sm:flex-row gap-3">
            {searchCanRetry && <button type="button" onClick={retrySearch} className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-lg">Réessayer cette requête</button>}
            <button type="button" onClick={returnToCriteria} className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-lg">Modifier les critères</button>
          </div>
        </div>
      ) : view === 'empty' ? (
        <div className="py-8 text-center space-y-4">
          <p>Aucun profil trouvé. Ajustez vos critères avant de relancer une recherche.</p>
          <button type="button" onClick={returnToCriteria} className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-lg">Modifier les critères</button>
        </div>
      ) : view === 'results' ? (
        <div className="space-y-5">
          {downloadError && <div role="alert"><Alert variant="error">{downloadError}</Alert></div>}
          {batchFeedback && (
            <div role="alert" className="space-y-3">
              <Alert variant="error">{batchFeedback.message}</Alert>
              {pendingBatchRefresh && (
                <button type="button" onClick={retryBatchRefresh} className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-lg">
                  Actualiser la liste des candidats
                </button>
              )}
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
            <div>
              <h3 ref={stateHeadingRef} tabIndex="-1" className="text-lg font-semibold">Profils trouvés</h3>
              <p>{results.count} {results.count === 1 ? 'profil trouvé' : 'profils trouvés'}</p>
              <p className="text-sm text-gray-600">Sélection disponible pendant 45 minutes maximum.</p>
            </div>
            <button type="button" onClick={returnToCriteria} className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-lg">Modifier les critères</button>
          </div>
          <div role="group" aria-label="Sélectionner des profils Turnover-IT" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {results.items.map((item, index) => {
              const displayName = item.displayName || 'Profil anonymisé';
              const selected = selectedTokens.includes(item.selectionToken);
              return (
                <div key={`${item.selectionToken}-${index}`} className={`min-h-[44px] p-4 border-2 rounded-lg focus-within:ring-2 focus-within:ring-blue-600 ${selected ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`}>
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => {
                        setSelectedTokens(previous => previous.includes(item.selectionToken)
                          ? previous.filter(token => token !== item.selectionToken)
                          : [...previous, item.selectionToken]);
                        setBatchFeedback(null);
                      }}
                      aria-label={`Sélectionner ${displayName}`}
                    />
                    <div className="space-y-2 min-w-0 flex-1">
                      <h4 className="font-semibold">{displayName}</h4>
                      <p>{item.title || 'Intitulé non renseigné'}</p>
                      {!!item.skills?.length && <div className="flex flex-wrap gap-2">{item.skills.map(skill => <span key={skill} className="px-2 py-1 text-sm bg-gray-100 rounded-full">{skill}</span>)}</div>}
                      {item.experienceYears && <p>{item.experienceYears} d’expérience</p>}
                      {item.location && <p>{item.location}</p>}
                      {item.availability && <p>{item.availability}</p>}
                      {item.dailySalary !== null && item.dailySalary !== undefined && <p>TJM souhaité : {item.dailySalary} € / jour</p>}
                      {selected && <p className="inline-flex items-center font-medium text-blue-800"><CheckCircle size={16} className="mr-1" aria-hidden="true" />Profil sélectionné</p>}
                    </div>
                  </label>
                  <div className="mt-4 flex justify-end border-t border-gray-200 pt-3">
                    <button
                      type="button"
                      onClick={() => downloadCv(item)}
                      disabled={Boolean(downloadingToken)}
                      className="inline-flex min-h-[44px] items-center rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50 focus:ring-2 focus:ring-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={`Télécharger le CV de ${displayName}`}
                    >
                      {downloadingToken === item.selectionToken
                        ? <Loader size={16} className="mr-2 animate-spin" aria-hidden="true" />
                        : <Download size={16} className="mr-2" aria-hidden="true" />}
                      Télécharger le CV
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="sticky bottom-0 bg-white pt-3 flex justify-end">
            <button type="button" disabled={!selectedTokens.length} onClick={runImport} className="min-h-[44px] px-5 py-3 bg-blue-600 text-white rounded-lg disabled:opacity-50 focus:ring-2 focus:ring-offset-2 focus:ring-blue-600">
              {batchFeedback
                ? 'Réessayer l’import'
                : selectedTokens.length > 1
                  ? `Ajouter et analyser ${selectedTokens.length} profils`
                  : 'Ajouter et analyser'}
            </button>
          </div>
        </div>
      ) : view === 'importing' ? (
        <div aria-live="polite" aria-busy="true" className="py-8 text-center space-y-3">
          <Loader className="mx-auto animate-spin text-blue-600" aria-hidden="true" />
          <h3 className="font-semibold">Ajout et analyse {selectedTokens.length > 1 ? 'des profils' : 'du profil'}…</h3>
          <p>
            {importProgress
              ? `${importProgress.completed + 1} / ${importProgress.total} · ${importProgress.displayName || 'Profil anonymisé'}`
              : `Ajout de ${selectedItem?.displayName || 'Profil anonymisé'}`}
          </p>
          <ul className="inline-block text-left list-disc"><li>Import du CV sécurisé</li><li>Ajout au besoin</li><li>Démarrage de l’analyse</li></ul>
        </div>
      ) : view === 'analyzing' ? (
        <div aria-live="polite" aria-busy="true" className="py-8 text-center space-y-3">
          <Loader className="mx-auto animate-spin text-blue-600" aria-hidden="true" />
          <h3 className="font-semibold">Profil ajouté. Analyse du CV en cours…</h3>
          <p>Cette fenêtre se fermera à la fin de l’analyse.</p>
        </div>
      ) : view === 'importError' ? (
        <div role="alert" className="space-y-4">
          <Alert variant="error">{error.message}</Alert>
          <div className="flex flex-col sm:flex-row gap-3">
            {importCanRetry && <button type="button" onClick={runImport} className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-lg">Réessayer l’import</button>}
            <button type="button" onClick={() => { setError(null); setView('results'); }} className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-lg">Revenir aux résultats</button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default TurnoverCandidateTab;
